import React, { useState } from 'react';
import {
  Search,
  Plus,
  ArrowRight,
  MapPin,
  Globe,
  Link2,
  Sparkles,
  MoreVertical,
  Layers,
  FileText,
  HelpCircle,
  BarChart2,
  Clock
} from 'lucide-react';
import { KeywordResearchProject, AuditInstance } from '../../types';
import { Button } from '../UI/Button';

interface KeywordProjectListProps {
  projects: KeywordResearchProject[];
  audits: AuditInstance[];
  onSelectProject: (id: string) => void;
  onCreateProject: (name: string, url: string, location: string, linkedAuditId?: string) => void;
  onDeleteProject: (id: string) => void;
}

export const KeywordProjectList: React.FC<KeywordProjectListProps> = ({
  projects,
  audits,
  onSelectProject,
  onCreateProject,
  onDeleteProject,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sourceMode, setSourceMode] = useState<'audit' | 'new'>('audit');
  const [selectedAuditId, setSelectedAuditId] = useState<string>(audits[0]?.id || '');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [loc, setLoc] = useState('Scottsdale, AZ');

  const [searchQuery, setSearchQuery] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Handle audit selection change
  const handleAuditSelect = (auditId: string) => {
    setSelectedAuditId(auditId);
    const found = audits.find(a => a.id === auditId);
    if (found) {
      setName(found.clientName);
      setUrl(found.websiteUrl);
    }
  };

  const handleOpenModal = () => {
    if (audits.length > 0) {
      setSourceMode('audit');
      handleAuditSelect(audits[0].id);
    } else {
      setSourceMode('new');
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !url.trim()) return;

    const linkedAuditId = sourceMode === 'audit' ? selectedAuditId : undefined;
    onCreateProject(name.trim(), url.trim(), loc.trim(), linkedAuditId);
    setName('');
    setUrl('');
    setIsModalOpen(false);
  };

  // Calculate real aggregated summary metrics
  const totalProjectsCount = projects.length;
  const totalKeywordsCount = projects.reduce((acc, p) => acc + (p.masterKeywords?.length || p.seedKeywords?.length || 0), 0);
  const totalQuestionsCount = projects.reduce((acc, p) => acc + (p.aeoQuestions?.length || 0), 0);
  const totalBriefsCount = projects.reduce((acc, p) => acc + (p.contentBriefs?.length || 0), 0);

  // Filter projects by search
  const filteredProjects = projects.filter(p => {
    const q = searchQuery.toLowerCase();
    return p.clientName.toLowerCase().includes(q) || p.websiteUrl.toLowerCase().includes(q);
  });

  return (
    <div className="animate-fade-in" style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px 80px 24px' }}>
      
      {/* 1. HERO SECTION (TWO COLUMNS) */}
      <div
        style={{
          background: '#090e1a',
          border: '1px solid #1e293b',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '24px',
        }}
      >
        <div style={{ flex: '1 1 560px' }}>
          {/* Metadata Badges */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10px', fontWeight: 800, color: '#f43f5e', background: 'rgba(244, 63, 94, 0.12)', border: '1px solid rgba(244, 63, 94, 0.3)', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              ZYNEX NINJA ENGINE
            </span>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', background: 'rgba(30, 41, 59, 0.6)', border: '1px solid #1e293b', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
              MODULE 5 · WEBSITE OPTIMIZATION
            </span>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '3px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
              PART 1 · KEYWORD RESEARCH ENGINE
            </span>
          </div>

          <h1 style={{ fontSize: '32px', color: '#ffffff', fontWeight: 800, margin: '0 0 10px 0', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            Practical Local SEO Keyword Research
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0, maxWidth: '680px', lineHeight: 1.6 }}>
            Conduct structured keyword research across 20 phases, from seed keywords through clustering, intent, SERP analysis, questions, briefs, and final keyword strategy.
          </p>
        </div>

        {/* Right Action & Callout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div
            style={{
              background: '#070a12',
              border: '1px solid #1e293b',
              borderRadius: '12px',
              padding: '12px 20px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '22px', fontWeight: 900, color: '#38bdf8', fontFamily: 'var(--font-heading)' }}>
              20
            </div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Research Phases
            </div>
          </div>

          <Button variant="primary" size="lg" icon={<Plus size={18} />} onClick={handleOpenModal}>
            + Start New Keyword Research
          </Button>
        </div>
      </div>

      {/* 2. COMPACT RESEARCH SUMMARY BAR */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}
      >
        <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#38bdf8' }}>
            <BarChart2 size={20} style={{ margin: 'auto' }} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              ACTIVE PROJECTS
            </div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#ffffff' }}>
              {totalProjectsCount}
            </div>
          </div>
        </div>

        <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
            <Layers size={20} style={{ margin: 'auto' }} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              KEYWORDS IDENTIFIED
            </div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#ffffff' }}>
              {totalKeywordsCount}
            </div>
          </div>
        </div>

        <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}>
            <HelpCircle size={20} style={{ margin: 'auto' }} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              RESEARCH QUESTIONS
            </div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#ffffff' }}>
              {totalQuestionsCount}
            </div>
          </div>
        </div>

        <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(129, 140, 248, 0.15)', border: '1px solid rgba(129, 140, 248, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#818cf8' }}>
            <FileText size={20} style={{ margin: 'auto' }} />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              CONTENT BRIEFS
            </div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#ffffff' }}>
              {totalBriefsCount}
            </div>
          </div>
        </div>
      </div>

      {/* 3. ACTIVE PROJECTS HEADER & CONTROLS */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontSize: '20px', color: '#ffffff', fontWeight: 800, margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
            ACTIVE RESEARCH PROJECTS
          </h2>
          <div style={{ fontSize: '13px', color: '#64748b' }}>
            {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'} available
          </div>
        </div>

        {/* Controls: Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ position: 'relative', width: '260px' }}>
            <Search size={15} color="#64748b" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                background: '#090e1a',
                border: '1px solid #1e293b',
                borderRadius: '8px',
                padding: '8px 12px 8px 36px',
                fontSize: '13px',
                color: '#ffffff',
                outline: 'none',
              }}
            />
          </div>
        </div>
      </div>

      {/* 4. REDESIGNED PROJECT CARDS GRID */}
      {filteredProjects.length === 0 ? (
        <div
          style={{
            background: '#090e1a',
            border: '1px solid #1e293b',
            borderRadius: '16px',
            padding: '48px 32px',
            textAlign: 'center',
            marginBottom: '40px',
          }}
        >
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto', color: '#38bdf8' }}>
            <Search size={28} />
          </div>
          <h3 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 700, margin: '0 0 8px 0' }}>
            NO ACTIVE RESEARCH PROJECTS
          </h3>
          <p style={{ color: '#94a3b8', fontSize: '14px', maxWidth: '460px', margin: '0 auto 24px auto', lineHeight: 1.5 }}>
            Start a structured 20-phase keyword research project for a website.
          </p>
          <Button variant="primary" icon={<Plus size={18} />} onClick={handleOpenModal}>
            + Start New Keyword Research
          </Button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', marginBottom: '48px' }}>
          {filteredProjects.map((proj) => {
            const linkedAudit = audits.find(a => a.id === proj.linkedAuditId);
            const currentPhase = proj.currentPhase || 1;
            const progressPct = Math.round((currentPhase / 20) * 100);

            let statusLabel = 'IN PROGRESS';
            let statusColor = '#38bdf8';
            let statusBg = 'rgba(2, 132, 199, 0.15)';
            let statusBorder = 'rgba(56, 189, 248, 0.3)';

            if (currentPhase >= 20) {
              statusLabel = 'COMPLETE';
              statusColor = '#10b981';
              statusBg = 'rgba(16, 185, 129, 0.15)';
              statusBorder = 'rgba(16, 185, 129, 0.3)';
            } else if (currentPhase === 0) {
              statusLabel = 'NOT STARTED';
              statusColor = '#64748b';
              statusBg = 'rgba(30, 41, 59, 0.6)';
              statusBorder = '#1e293b';
            }

            const kwCount = proj.masterKeywords?.length || proj.seedKeywords?.length || 0;
            const questionCount = proj.aeoQuestions?.length || 0;
            const briefCount = proj.contentBriefs?.length || 0;

            return (
              <div
                key={proj.id}
                onClick={() => onSelectProject(proj.id)}
                style={{
                  background: '#090e1a',
                  border: '1px solid #1e293b',
                  borderRadius: '16px',
                  padding: '24px',
                  cursor: 'pointer',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(56, 189, 248, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#1e293b';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div>
                  {/* Top Card Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', fontFamily: 'var(--font-mono)' }}>
                        PHASE {String(currentPhase).padStart(2, '0')} / 20
                      </span>
                      <span style={{ fontSize: '10px', fontWeight: 800, color: statusColor, background: statusBg, border: `1px solid ${statusBorder}`, padding: '2px 8px', borderRadius: '4px' }}>
                        ● {statusLabel}
                      </span>
                    </div>

                    {/* Context Menu Dropdown */}
                    <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenMenuId(openMenuId === proj.id ? null : proj.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#64748b',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '4px',
                          display: 'flex',
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>

                      {openMenuId === proj.id && (
                        <div
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: '24px',
                            background: '#070a12',
                            border: '1px solid #1e293b',
                            borderRadius: '8px',
                            padding: '6px',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                            zIndex: 100,
                            minWidth: '130px',
                          }}
                        >
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              if (confirm(`Delete keyword project for "${proj.clientName}"?`)) {
                                onDeleteProject(proj.id);
                              }
                            }}
                            style={{
                              width: '100%',
                              background: 'none',
                              border: 'none',
                              color: '#f43f5e',
                              fontSize: '12px',
                              fontWeight: 600,
                              padding: '6px 10px',
                              borderRadius: '4px',
                              textAlign: 'left',
                              cursor: 'pointer',
                            }}
                          >
                            Delete Project
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Project Name */}
                  <h3 style={{ fontSize: '20px', color: '#ffffff', fontWeight: 800, margin: '0 0 6px 0', letterSpacing: '-0.01em' }}>
                    {proj.clientName}
                  </h3>

                  {/* Domain & Location */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', fontSize: '12.5px', color: '#94a3b8', marginBottom: '16px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#38bdf8' }}>
                      <Globe size={13} />
                      <span>{proj.websiteUrl}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#64748b' }}>
                      <MapPin size={13} color="#10b981" />
                      <span>{proj.businessProfile?.primaryLocation || 'Scottsdale, AZ'}</span>
                    </div>
                  </div>

                  {/* Linked Audit Badge */}
                  {linkedAudit && (
                    <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.25)', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', color: '#10b981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
                      <Link2 size={12} />
                      <span>Linked to Technical Audit ({linkedAudit.overallScore ?? 100}% Health Score)</span>
                    </div>
                  )}

                  {/* Segmented Progress Bar */}
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 700, color: '#94a3b8', marginBottom: '6px' }}>
                      <span>Workflow Completion</span>
                      <span style={{ color: '#38bdf8', fontFamily: 'var(--font-mono)' }}>{progressPct}%</span>
                    </div>
                    <div style={{ width: '100%', height: '6px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${progressPct}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)',
                          borderRadius: '3px',
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </div>
                  </div>

                  {/* Metrics Pills Row */}
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#64748b', paddingBottom: '16px', borderBottom: '1px solid #1e293b' }}>
                    <span><strong style={{ color: '#ffffff' }}>{kwCount}</strong> Keywords</span>
                    <span>•</span>
                    <span><strong style={{ color: '#ffffff' }}>{questionCount}</strong> Questions</span>
                    <span>•</span>
                    <span><strong style={{ color: '#ffffff' }}>{briefCount}</strong> Briefs</span>
                  </div>
                </div>

                {/* Footer Row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Clock size={12} />
                    <span>Last activity: Phase {currentPhase}</span>
                  </div>

                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Open Keyword Workspace <ArrowRight size={15} />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 5. 20-PHASE RESEARCH PIPELINE VISUALIZATION */}
      <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '16px', padding: '28px', marginBottom: '40px' }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            STRUCTURED RESEARCH ENGINE
          </div>
          <h3 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 800, margin: 0 }}>
            20-Phase Keyword Strategy Pipeline
          </h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
          {[
            { phase: '01', title: 'Seed Terms', desc: 'Core Services' },
            { phase: '02', title: 'Intent Map', desc: 'Search Intent' },
            { phase: '03', title: 'Competitor Gaps', desc: 'SERP Analysis' },
            { phase: '04', title: 'Variations', desc: 'Long-Tail Ext.' },
            { phase: '05', title: 'Clustering', desc: 'Topic Clusters' },
            { phase: '06', title: 'Content Briefs', desc: 'Page Specs' },
            { phase: '07–20', title: 'Final Strategy', desc: 'Master Plan' },
          ].map((item, idx) => (
            <div
              key={idx}
              style={{
                background: '#070a12',
                border: '1px solid #1e293b',
                borderRadius: '10px',
                padding: '12px 14px',
                position: 'relative',
              }}
            >
              <div style={{ fontSize: '10px', fontWeight: 800, color: '#38bdf8', fontFamily: 'var(--font-mono)', marginBottom: '4px' }}>
                PHASE {item.phase}
              </div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginBottom: '2px' }}>
                {item.title}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>
                {item.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 6. RESEARCH WORKFLOW ARCHITECTURE GUIDE */}
      <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '16px', padding: '28px' }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '11px', fontWeight: 800, color: '#10b981', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
            METHODOLOGY & EXECUTION
          </div>
          <h3 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 800, margin: 0 }}>
            Zynex Ninja 4-Stage Research Architecture
          </h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
          <div style={{ background: '#070a12', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8', marginBottom: '6px' }}>
              STAGE 1 · PHASES 1–5
            </div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
              Foundation & Niche Mapping
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5 }}>
              Extract seed terms, profile target business services, map client location hubs, and set baseline search targets.
            </div>
          </div>

          <div style={{ background: '#070a12', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8', marginBottom: '6px' }}>
              STAGE 2 · PHASES 6–10
            </div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
              Expansion & Competitor Gaps
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5 }}>
              Harvest long-tail variations, analyze competitor ranking matrices, and extract AEO user search question patterns.
            </div>
          </div>

          <div style={{ background: '#070a12', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8', marginBottom: '6px' }}>
              STAGE 3 · PHASES 11–15
            </div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
              Clustering & Intent Overlap
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5 }}>
              Group keywords by search intent (Informational, Transactional), establish SERP overlap, and prevent keyword cannibalization.
            </div>
          </div>

          <div style={{ background: '#070a12', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', marginBottom: '6px' }}>
              STAGE 4 · PHASES 16–20
            </div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>
              Content Specs & Strategy
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5 }}>
              Generate complete page content briefs (H1/H2 structures, metadata, semantic terms) and output the master strategy roadmap.
            </div>
          </div>
        </div>
      </div>

      {/* 7. MODAL FOR STARTING / LINKING PROJECTS */}
      {isModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '16px', maxWidth: '520px', width: '100%', padding: '30px', boxShadow: '0 20px 50px rgba(0,0,0,0.6)' }}>
            <h3 style={{ fontSize: '20px', color: '#ffffff', fontWeight: 800, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} color="#38bdf8" />
              <span>Start Keyword Research Project</span>
            </h3>

            {/* Mode Selection Tabs */}
            <div style={{ display: 'flex', background: '#070a12', padding: '4px', borderRadius: '8px', border: '1px solid #1e293b', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={() => {
                  setSourceMode('audit');
                  if (audits.length > 0) handleAuditSelect(audits[0].id);
                }}
                style={{
                  flex: 1,
                  padding: '8px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: sourceMode === 'audit' ? '#0284c7' : 'transparent',
                  color: sourceMode === 'audit' ? '#ffffff' : '#94a3b8',
                }}
              >
                🔗 Link Existing SEO Audit ({audits.length})
              </button>

              <button
                type="button"
                onClick={() => {
                  setSourceMode('new');
                  setName('');
                  setUrl('');
                }}
                style={{
                  flex: 1,
                  padding: '8px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: sourceMode === 'new' ? '#0284c7' : 'transparent',
                  color: sourceMode === 'new' ? '#ffffff' : '#94a3b8',
                }}
              >
                ➕ Start New Blank Project
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {sourceMode === 'audit' ? (
                audits.length === 0 ? (
                  <div style={{ padding: '14px', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '8px', color: '#f59e0b', fontSize: '13px', marginBottom: '20px' }}>
                    No completed SEO audits found. Select "Start New Blank Project" above or complete an audit first.
                  </div>
                ) : (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                      Select Existing Completed SEO Audit *
                    </label>
                    <select
                      value={selectedAuditId}
                      onChange={(e) => handleAuditSelect(e.target.value)}
                      style={{ width: '100%', background: '#070a12', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', color: '#ffffff', fontSize: '13px', outline: 'none' }}
                    >
                      {audits.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.clientName} ({a.websiteUrl}) — Score: {a.overallScore ?? 100}%
                        </option>
                      ))}
                    </select>
                  </div>
                )
              ) : null}

              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                  Client / Business Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Apex General Contracting"
                  value={name}
                  required
                  onChange={(e) => setName(e.target.value)}
                  style={{ width: '100%', background: '#070a12', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', color: '#ffffff', fontSize: '13px', outline: 'none' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                  Website URL *
                </label>
                <input
                  type="url"
                  placeholder="https://clientwebsite.com"
                  value={url}
                  required
                  onChange={(e) => setUrl(e.target.value)}
                  style={{ width: '100%', background: '#070a12', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', color: '#ffffff', fontSize: '13px', outline: 'none' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: '6px' }}>
                  Primary Target Location (City, State)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Scottsdale, AZ or Miami, FL"
                  value={loc}
                  onChange={(e) => setLoc(e.target.value)}
                  style={{ width: '100%', background: '#070a12', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 12px', color: '#ffffff', fontSize: '13px', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  {sourceMode === 'audit' ? 'Link Audit & Open Workspace' : 'Create Blank Project'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
